import React from 'react';
import { Link } from 'react-router-dom';
import { Heart, Mail, Phone, MapPin, Facebook, Instagram, Youtube, ArrowRight } from 'lucide-react';

const Footer: React.FC = () => {
  return (
    <footer className="bg-deep-forest text-white">
      {/* Newsletter Section */}
      <div className="bg-sky-blue/10 py-12">
        <div className="max-w-7xl mx-auto px-4">
          <div className="bg-sky-blue rounded-2xl p-8 text-center">
            <h3 className="font-display text-3xl text-white mb-4">Join Our Pack!</h3>
            <p className="text-white/90 mb-6 max-w-2xl mx-auto">
              Stay updated with heartwarming stories, adoption events, and ways to help animals in need.
            </p>
            <form className="flex flex-col sm:flex-row gap-4 max-w-md mx-auto">
              <input
                type="email"
                placeholder="Your email address"
                className="flex-1 px-4 py-3 rounded-full text-deep-forest"
              />
              <button
                type="submit"
                className="bg-warm-coral hover:bg-warm-coral/90 text-white px-6 py-3 rounded-full font-semibold flex items-center justify-center gap-2 transition-colors"
              >
                Subscribe
                <ArrowRight size={18} />
              </button>
            </form>
          </div>
        </div>
      </div>

      {/* Main Footer Content */}
      <div className="max-w-7xl mx-auto px-4 py-12">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {/* About Column */}
          <div>
            <div className="flex items-center space-x-3 mb-6">
              <div className="bg-white rounded-full p-2">
                <img 
                  src="/logo.png" 
                  alt="Animal Allies Logo" 
                  className="h-6 w-6 object-contain"
                />
              </div>
              <div>
                <h2 className="font-display text-xl">Animal Allies</h2>
                <p className="text-xs text-white/70">Humane Society</p>
              </div>
            </div>
            <p className="text-white/80 mb-4 text-sm">
              Dedicated to a compassionate journey for pets and people in our community.
              A loving home for every pet since 1963.
            </p>
            <div className="flex space-x-3">
              <a href="https://facebook.com" className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors">
                <Facebook size={20} />
              </a>
              <a href="https://instagram.com" className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors">
                <Instagram size={20} />
              </a>
              <a href="https://youtube.com" className="bg-white/10 hover:bg-white/20 p-2 rounded-full transition-colors">
                <Youtube size={20} />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="font-ui font-semibold text-lg mb-4">Quick Links</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/adopt/available" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Available Pets
                </Link>
              </li>
              <li>
                <Link to="/volunteer" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Volunteer Opportunities
                </Link>
              </li>
              <li>
                <Link to="/foster" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Become a Foster
                </Link>
              </li>
              <li>
                <Link to="/events" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Upcoming Events
                </Link>
              </li>
              <li>
                <Link to="/donate" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Ways to Give
                </Link>
              </li>
              <li>
                <Link to="/services/lost-found" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Lost & Found Pets
                </Link>
              </li>
            </ul>
          </div>

          {/* Resources */}
          <div>
            <h3 className="font-ui font-semibold text-lg mb-4">Resources</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/resources/pet-care" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Pet Care Tips
                </Link>
              </li>
              <li>
                <Link to="/resources/training" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Training Resources
                </Link>
              </li>
              <li>
                <Link to="/resources/vets" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Area Veterinarians
                </Link>
              </li>
              <li>
                <Link to="/resources/emergency" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Emergency Contacts
                </Link>
              </li>
              <li>
                <Link to="/about/stories" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  Success Stories
                </Link>
              </li>
              <li>
                <Link to="/about/newsletter" className="text-white/80 hover:text-warm-coral transition-colors text-sm">
                  The Paw Print Newsletter
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="font-ui font-semibold text-lg mb-4">Visit Us</h3>
            <div className="space-y-3 text-sm">
              <div className="flex items-start gap-3">
                <MapPin size={18} className="text-warm-coral mt-0.5" />
                <div>
                  <p className="text-white/80">4006 Airport Road</p>
                  <p className="text-white/80">Duluth, MN 55811</p>
                </div>
              </div>
              <div className="flex items-center gap-3">
                <Phone size={18} className="text-warm-coral" />
                <p className="text-white/80">218-722-5341</p>
              </div>
              <div className="flex items-center gap-3">
                <Mail size={18} className="text-warm-coral" />
                <p className="text-white/80">info@animalallies.net</p>
              </div>
            </div>
            <div className="mt-4 pt-4 border-t border-white/10">
              <h4 className="font-semibold mb-2">Adoption Hours</h4>
              <p className="text-white/80 text-sm">Tue-Fri: 12:00-6:00pm</p>
              <p className="text-white/80 text-sm">Sat-Sun: 12:00-4:00pm</p>
              <p className="text-white/80 text-sm">Closed Mondays</p>
            </div>
          </div>
        </div>
      </div>

      {/* Bottom Bar */}
      <div className="border-t border-white/10">
        <div className="max-w-7xl mx-auto px-4 py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <div className="text-white/60 text-sm text-center md:text-left">
              © 2024 Animal Allies Humane Society. All rights reserved. | Tax ID: 41-0917362
            </div>
            <div className="flex gap-6 text-sm">
              <Link to="/privacy" className="text-white/60 hover:text-warm-coral transition-colors">
                Privacy Policy
              </Link>
              <Link to="/terms" className="text-white/60 hover:text-warm-coral transition-colors">
                Terms of Use
              </Link>
              <Link to="/accessibility" className="text-white/60 hover:text-warm-coral transition-colors">
                Accessibility
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;